import React, { FC } from 'react';
import s from './TemplateMediaContainer.scss';
import cs from 'classnames';
import { EditButton } from '../EditButton/EditButton';
import { isBlankTemplate, Template } from '../../domain/entities/template';
import { isInspirationalTemplate } from '../../domain/entities/inspirationalTemplate';
import { ViewButton } from '../ViewButton/ViewButton';
import { useTranslation } from '@wix/wix-i18n-config';
import { useViewUrl } from '../../hooks/useViewUrl';
import { useIsMounted } from '../../hooks/useIsMounted';
import { useBiLogger } from '../../contexts/BILoggerContext';
import { useInitialState } from '../../contexts/InitialStateContext';
import { usePreviewTemplate } from '../../contexts/PreviewTemplateContext';
import { templateMediaContainerDataHooks } from './TemplateMediaContainerDataHooks';
import { useIsStudio2Ui } from '../../hooks/useIsStudio2Ui';
import { CategorySlug } from '../../config';

export type TemplateMediaContainerProps = {
  children?: React.ReactNode;
  template: Template;
  index?: number;
  isLoading?: boolean;
  categorySlug?: string;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
};

export const TemplateMediaContainer: FC<TemplateMediaContainerProps> = ({
  children,
  template,
  index,
  categorySlug,
  onMouseEnter,
  onMouseLeave,
  isLoading,
}) => {
  const { t } = useTranslation();
  const viewUrl = useViewUrl(template);
  const isMounted = useIsMounted();
  const biLogger = useBiLogger();
  const { isMobile } = useInitialState();
  const { setPreviewTemplate } = usePreviewTemplate();
  const isStudio2Ui = useIsStudio2Ui();
  // the studio2 carousel card shows its own view and edit buttons
  const withActions = !(
    isStudio2Ui && categorySlug === CategorySlug.INSPIRATIONAL
  );
  const buttonSize = isStudio2Ui ? 'small' : 'large';
  // inspirational templates are view-only in the old UI (#11906)
  const isEditable = isStudio2Ui || !isInspirationalTemplate(template);

  const onViewTemplateClick = () => {
    if (isMobile) {
      setPreviewTemplate(template);
    }
  };

  const onViewClick = () => {
    biLogger.reportViewTemplate({ ...template, index, categorySlug });
  };

  return (
    <div
      onClick={onViewTemplateClick}
      className={s.templateMediaContainer}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <div
        className={cs(s.templateMediaLoadingContainer, {
          [s.animationOverMedia]: isMounted,
          [s.loading]: isLoading,
        })}
      />
      {children}
      {!isMobile && (
        <a
          target="_blank"
          className={cs(s.viewLink, s.hideOnDesktop)}
          href={viewUrl}
          rel="noreferrer"
          onClick={onViewClick}
        >
          {t('app.templates.grid.view.button')}
        </a>
      )}
      {withActions && (
        <div className={s.buttonsOverlay}>
          <div className={s.buttonsContainer}>
            {isEditable && (
              <EditButton
                skin="light"
                size={buttonSize}
                template={template}
                categorySlug={categorySlug}
                index={index}
                dataHook={templateMediaContainerDataHooks.editButton({
                  key: template.templateId,
                })}
                className={s.editButton}
              />
            )}
            {!isBlankTemplate(template) && (
              <ViewButton
                skin="light"
                size={buttonSize}
                template={template}
                categorySlug={categorySlug}
                index={index}
                dataHook={templateMediaContainerDataHooks.viewButton({
                  key: template.templateId,
                })}
                className={s.viewButton}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};
